import { useState, useCallback, useMemo } from 'react';
import { ServiceRow, ENVIRONMENTS, isRecent, isRecent7Days, shouldShowWarning } from '../utils';
import { CombinedReleaseHistory } from '@internal/backstage-plugin-ewii-azure';

export type WarningFilter = 'no-filter' | 'acceptance' | 'production' | 'staged';

export interface SummaryFilter {
  type: 'recent' | 'health';
  timePeriod?: '24h' | '7d' | '30d';
  environment?: string;
}

export interface CustomDateRange {
  from: string;
  to: string;
}

export const useFilters = (services: ServiceRow[], releaseHistory: Record<string, CombinedReleaseHistory[]>) => {
  const [warningFilter, setWarningFilter] = useState<WarningFilter>('no-filter');
  const [summaryFilter, setSummaryFilter] = useState<SummaryFilter | null>(null);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange | null>(null);

  const hasCustomDateRangeWarning = useCallback((row: ServiceRow): boolean => {
    if (!customDateRange || !customDateRange.from || !customDateRange.to) return false;

    // Check if any environment has releases in the custom date range
    return ENVIRONMENTS.some(env => {
      const cell = row[env];
      if (typeof cell === 'object' && cell.componentName) {
        const customReleases = releaseHistory[`${env}_custom`] || [];
        return customReleases.some(release => release.component === cell.componentName);
      }
      return false;
    });
  }, [customDateRange, releaseHistory]);

  const has30DayReleaseWarning = useCallback((row: ServiceRow): boolean => {
    const releases30d = releaseHistory['test_30d'] || [];
    const releases30dAcceptance = releaseHistory['acceptance_30d'] || [];
    const releases30dProduction = releaseHistory['production_30d'] || [];
    
    return ENVIRONMENTS.some(env => {
      const cell = row[env];
      if (typeof cell === 'object' && cell.componentName) {
        const envReleases = env === 'test' ? releases30d : 
                           env === 'acceptance' ? releases30dAcceptance : 
                           env === 'production' ? releases30dProduction : [];
        return envReleases.some(release => release.component === cell.componentName);
      }
      return false;
    });
  }, [releaseHistory]);

  const hasRecentSummaryFilterWarning = useCallback((row: ServiceRow): boolean => {
    if (!summaryFilter) return false;

    if (summaryFilter.environment) {
      const releaseKey = `${summaryFilter.environment}_${summaryFilter.timePeriod}`;
      const releases = releaseHistory[releaseKey] || [];
      
      return ENVIRONMENTS.some(env => {
        const cell = row[env];
        if (typeof cell === 'object' && cell.componentName) {
          return releases.some(release => release.component === cell.componentName);
        }
        return false;
      });
    } else {
      if (summaryFilter.timePeriod === '30d') {
        return has30DayReleaseWarning(row);
      } else {
        const isRecentFunction = summaryFilter.timePeriod === '7d' ? isRecent7Days : isRecent;
        return ENVIRONMENTS.some(env => {
          const cell = row[env];
          return typeof cell === 'object' && isRecentFunction(cell.buildDate);
        });
      }
    }
  }, [summaryFilter, releaseHistory, has30DayReleaseWarning]);

  const hasHealthSummaryFilterWarning = useCallback((row: ServiceRow): boolean => {
    if (!summaryFilter) return false;

    if (summaryFilter.environment) {
      const cell = row[summaryFilter.environment];
      return Boolean(typeof cell === 'object' && cell.healthStatus?.some(status => status.status === 'Unhealthy'));
    } else {
      return ENVIRONMENTS.some(env => {
        const cell = row[env];
        return Boolean(typeof cell === 'object' && cell.healthStatus?.some(status => status.status === 'Unhealthy'));
      });
    }
  }, [summaryFilter]);

  const hasSummaryFilterWarning = useCallback((row: ServiceRow): boolean => {
    if (!summaryFilter) return false;

    if (summaryFilter.type === 'recent') {
      return hasRecentSummaryFilterWarning(row);
    }
    
    if (summaryFilter.type === 'health') {
      return hasHealthSummaryFilterWarning(row);
    }

    return false;
  }, [summaryFilter, hasRecentSummaryFilterWarning, hasHealthSummaryFilterWarning]);

  const hasProductionWarning = useCallback((row: ServiceRow): boolean => {
    const productionCell = row['production'];
    const acceptanceCell = row['acceptance'];
    if (typeof productionCell === 'object' && 'version' in productionCell) {
      return shouldShowWarning(productionCell, acceptanceCell);
    }
    return false;
  }, []);

  const hasAcceptanceWarning = useCallback((row: ServiceRow): boolean => {
    const acceptanceCell = row['acceptance'];
    const testCell = row['test'];
    if (typeof acceptanceCell === 'object' && 'version' in acceptanceCell) {
      return shouldShowWarning(acceptanceCell, testCell);
    }
    return false;
  }, []);

  const hasStagedWarning = useCallback((row: ServiceRow): boolean => {
    // Check for staged releases in production environment
    const productionReleases = releaseHistory['production_30d'] || [];
    
    return ENVIRONMENTS.some(env => {
      const cell = row[env];
      if (typeof cell === 'object' && cell.componentName) {
        return productionReleases.some(release => 
          release.component === cell.componentName && 
          release.status === 'staged'
        );
      }
      return false;
    });
  }, [releaseHistory]);

  const hasWarning = useCallback((row: ServiceRow) => {
    if (customDateRange) {
      return hasCustomDateRangeWarning(row);
    }

    if (summaryFilter) {
      return hasSummaryFilterWarning(row);
    }

    switch (warningFilter) {
      case 'production':
        return hasProductionWarning(row);
      case 'acceptance':
        return hasAcceptanceWarning(row);
      case 'staged':
        return hasStagedWarning(row);
      default:
        return false;
    }
  }, [customDateRange, summaryFilter, warningFilter, hasCustomDateRangeWarning, hasSummaryFilterWarning, hasProductionWarning, hasAcceptanceWarning, hasStagedWarning]);

  const filteredServices = useMemo(() => {
    return (warningFilter === 'no-filter' && !summaryFilter && (!customDateRange || !customDateRange.from || !customDateRange.to)) 
      ? services 
      : services.filter(hasWarning);
  }, [services, warningFilter, summaryFilter, customDateRange, hasWarning]);

  return {
    warningFilter,
    setWarningFilter,
    summaryFilter,
    setSummaryFilter,
    customDateRange,
    setCustomDateRange,
    filteredServices
  };
}; 